import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabase';
import { SegmentedPills, PrimaryBtn, SecondaryBtn } from '../systemKit';
import InfoDot from '../InfoDot';
import { csvCell } from '../../lib/csv';
import TopSpend from './TopSpend';
import TopMovers from './TopMovers';

// TopReports — the report-type controls + Run-report flow + result panel
// for the two new Invoices > Reports report types: Top spend and Top movers.
//
// Lives inside the existing Invoices page Reports tab (alongside Period
// summary and Single item history). Owns:
//   - Time range, row count, vendor multi-select, polarity, sort controls
//   - Run report → calls the relevant cost_dashboard_* RPC
//   - Filter (vendor + polarity) and sort applied client-side after fetch
//     so the operator can re-filter without a re-fetch
//   - "Print" + "Export CSV" handoff (parent passes the CSV builder; we
//     supply the raw rows)
//
// The presentational TopSpend / TopMovers components live in their own
// files — this component only orchestrates fetch + filter + sort and
// hands the final rows to the right presentational component.
//
// Row count: the RPC is asked for as many rows as the operator picked
// (capped at 200 for "All" — see Cap below). Vendor filter + polarity
// are applied AFTER the fetch in JS, so the visible count may be lower
// than the requested count. That's intentional — re-fetching every time
// the operator clicks a polarity chip would be wasteful.

const ROW_COUNT_CAP = 200;
const ROW_COUNT_OPTIONS = [
  { key: '10',  label: '10' },
  { key: '20',  label: '20' },
  { key: '50',  label: '50' },
  { key: 'all', label: 'All' },
];

const TIME_RANGE_OPTIONS = [
  { key: '7d',     label: '7d' },
  { key: '30d',    label: '30d' },
  { key: '90d',    label: '90d' },
  { key: 'custom', label: 'Custom' },
];

const POLARITY_OPTIONS = [
  { key: 'all',  label: 'All' },
  { key: 'up',   label: 'Up only ↑' },
  { key: 'down', label: 'Down only ↓' },
];

const SORT_TOP_SPEND = [
  { key: 'spend',    label: 'By total spend' },
  { key: 'unit',     label: 'By unit price' },
  { key: 'invoices', label: 'By invoice count' },
];

const SORT_TOP_MOVERS = [
  { key: 'impact', label: 'By dollar impact' },
  { key: 'pct',    label: 'By % change' },
  { key: 'volume', label: 'By volume' },
];

const COMPARISON_OPTIONS = [
  { key: 'prior',        label: 'Prior period' },
  { key: 'sameLastYear', label: 'Same period last year' },
  { key: 'custom',       label: 'Custom range' },
];

const COMPARISON_INFO = `How movers are calculated

Each item's current price is the average across this report's window. Each item's baseline is the median across the comparison window. Items rank by dollar impact: (current − baseline) × volume bought in this window. Larger dollar impact = bigger negotiation lever.`;

function todayIso() { return new Date().toISOString().split('T')[0]; }

function startDateForRange(rangeKey, customStart) {
  if (rangeKey === 'custom' && customStart) return customStart;
  const days = rangeKey === '7d' ? 7 : rangeKey === '90d' ? 90 : 30;
  const d = new Date();
  d.setDate(d.getDate() - (days - 1));
  return d.toISOString().split('T')[0];
}

// Compute baseline window dates from the comparison radio + report dates.
// Returns null for 'prior' so the RPC auto-derives — we mirror the same
// math client-side when we need to display the resolved window in an
// empty state, but we don't pass the dates explicitly when 'prior' so
// the server stays the source of truth for "prior" semantics.
function resolveBaselineWindow(reportStart, reportEnd, comparison, compStart, compEnd) {
  if (comparison === 'sameLastYear') {
    const s = new Date(reportStart + 'T00:00:00');
    const e = new Date(reportEnd + 'T00:00:00');
    s.setFullYear(s.getFullYear() - 1);
    e.setFullYear(e.getFullYear() - 1);
    return {
      start: s.toISOString().split('T')[0],
      end: e.toISOString().split('T')[0],
      explicit: true,
    };
  }
  if (comparison === 'custom' && compStart && compEnd) {
    return { start: compStart, end: compEnd, explicit: true };
  }
  // 'prior' → mirror the server's auto-derivation so the empty state can
  // still display the resolved window. explicit:false signals "don't pass
  // these dates to the RPC; let it auto-derive."
  const s = new Date(reportStart + 'T00:00:00');
  const e = new Date(reportEnd + 'T00:00:00');
  const days = Math.round((e - s) / 86400000) + 1;
  const priorEnd = new Date(s); priorEnd.setDate(priorEnd.getDate() - 1);
  const priorStart = new Date(priorEnd); priorStart.setDate(priorStart.getDate() - days + 1);
  return {
    start: priorStart.toISOString().split('T')[0],
    end: priorEnd.toISOString().split('T')[0],
    explicit: false,
  };
}

export default function TopReports({
  reportType,           // 'top-spend' | 'top-movers'
  companyId,
  onOpenDrillIn,
  onWatchlistChange,
}) {
  // ── Filter / sort state (per report instance) ─────────────────────────────
  // Reset on report-type switch — operator's "30d Up only" choice for movers
  // shouldn't carry into spend (which has different sort options).
  const [rangeKey, setRangeKey] = useState('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [rowCount, setRowCount] = useState('20');
  const [vendorFilter, setVendorFilter] = useState({ all: true, picked: new Set() });
  const [polarity, setPolarity] = useState('all');
  const [sort, setSort] = useState(reportType === 'top-spend' ? 'spend' : 'impact');

  // Comparison-window state (Top Movers only). Session-only — no prefs
  // persistence in v1. Default 'prior' matches the legacy auto-derived
  // baseline so first-time-Run-Report behavior is unchanged.
  const [comparison, setComparison] = useState('prior');  // 'prior' | 'sameLastYear' | 'custom'
  const [compStart, setCompStart] = useState('');
  const [compEnd, setCompEnd] = useState('');

  // Fetched data + run state
  const [rows, setRows] = useState(null);  // null = not yet run
  const [running, setRunning] = useState(false);
  const [lastRanRange, setLastRanRange] = useState(null);  // {start, end, label} captured at run
  const [lastBaselineWindow, setLastBaselineWindow] = useState(null);  // {start, end} captured at run
  const [vendors, setVendors] = useState([]);
  const [staleConfig, setStaleConfig] = useState(false);
  // baselineLineCount: null = unknown, 0 = empty (drives the "no comparison
  // data" empty state), >0 = comparison had data but movers query returned
  // 0 rows for other reasons (everything was flat / filtered out).
  const [baselineLineCount, setBaselineLineCount] = useState(null);

  // Reset filter/sort when report-type flips. State below is identity-stable
  // to the (reportType, companyId) pair.
  useEffect(() => {
    setRangeKey('30d');
    setCustomStart(''); setCustomEnd('');
    setRowCount('20');
    setVendorFilter({ all: true, picked: new Set() });
    setPolarity('all');
    setSort(reportType === 'top-spend' ? 'spend' : 'impact');
    setComparison('prior');
    setCompStart(''); setCompEnd('');
    setRows(null);
    setLastRanRange(null);
    setLastBaselineWindow(null);
    setBaselineLineCount(null);
    setStaleConfig(false);
  }, [reportType]);

  // Pull distinct vendors (last 90 days — enough breadth for the picker
  // without dragging every historical vendor name in).
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      const since = (() => {
        const d = new Date(); d.setDate(d.getDate() - 90);
        return d.toISOString().split('T')[0];
      })();
      const { data } = await supabase
        .from('invoice_line_items')
        .select('vendor_name')
        .eq('company_id', companyId)
        .gte('invoice_date', since)
        .not('vendor_name', 'is', null);
      if (cancelled) return;
      const set = new Set();
      (data || []).forEach(r => { if (r.vendor_name) set.add(r.vendor_name); });
      setVendors(Array.from(set).sort());
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  // Mark config as stale once the operator changes anything after a run.
  useEffect(() => {
    if (rows == null) return;
    setStaleConfig(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey, customStart, customEnd, rowCount, comparison, compStart, compEnd]);

  // Validation: custom range with start > end disables Run report.
  const customRangeInvalid =
    rangeKey === 'custom' && customStart && customEnd && customStart > customEnd;

  // Comparison-window validation (movers only). Mirrors the report-window
  // validation: dates must be a valid order, can't be in the future.
  const compRangeInvalid =
    reportType === 'top-movers' && comparison === 'custom'
    && compStart && compEnd && compStart > compEnd;
  const compInFuture =
    reportType === 'top-movers' && comparison === 'custom'
    && (compStart > todayIso() || compEnd > todayIso());
  const compNeedsDates =
    reportType === 'top-movers' && comparison === 'custom'
    && (!compStart || !compEnd);

  // Effective limit for the RPC. "All" caps at 200 to prevent dumps.
  const effectiveLimit = rowCount === 'all' ? ROW_COUNT_CAP : Math.max(10, parseInt(rowCount, 10) || 20);

  // runReport accepts overrides so empty-state CTAs can flip the comparison
  // radio AND fire a fresh run in the same handler — React state batching
  // would otherwise leave runReport reading the old `comparison` value.
  async function runReport(overrides = {}) {
    if (running || !companyId) return;
    if (customRangeInvalid) return;
    const cmp     = overrides.comparison ?? comparison;
    const cs      = overrides.compStart  ?? compStart;
    const ce      = overrides.compEnd    ?? compEnd;
    const cmpInvalid = reportType === 'top-movers' && cmp === 'custom'
      && cs && ce && cs > ce;
    const cmpFuture  = reportType === 'top-movers' && cmp === 'custom'
      && (cs > todayIso() || ce > todayIso());
    const cmpMissing = reportType === 'top-movers' && cmp === 'custom'
      && (!cs || !ce);
    if (cmpInvalid || cmpFuture || cmpMissing) return;

    setRunning(true);
    setStaleConfig(false);
    setBaselineLineCount(null);

    const start = startDateForRange(rangeKey, customStart);
    const end = rangeKey === 'custom' && customEnd ? customEnd : todayIso();
    const fn = reportType === 'top-spend'
      ? 'cost_dashboard_top_spend'
      : 'cost_dashboard_top_impact_movers';

    const params = {
      p_company_id: companyId,
      p_start_date: start,
      p_end_date: end,
      p_limit: effectiveLimit,
    };

    let baselineWin = null;
    if (reportType === 'top-movers') {
      baselineWin = resolveBaselineWindow(start, end, cmp, cs, ce);
      if (baselineWin.explicit) {
        params.p_baseline_start_date = baselineWin.start;
        params.p_baseline_end_date   = baselineWin.end;
      }
      setLastBaselineWindow({ start: baselineWin.start, end: baselineWin.end });
    } else {
      setLastBaselineWindow(null);
    }

    const { data, error } = await supabase.rpc(fn, params);
    setLastRanRange({ start, end, label: `${start} to ${end}` });

    if (error) {
      setRows([]);
      setRunning(false);
      return;
    }

    const rowsResult = data || [];
    setRows(rowsResult);

    // Disambiguate empty state on Top Movers — fire the count helper only
    // when we got 0 rows. Saves a round trip on the happy path. count=0
    // means "comparison window had no usable invoices" (CTA flow); count>0
    // means "comparison had data but nothing rose to the surface" (existing
    // 'no comparable price movement' empty state).
    if (reportType === 'top-movers' && rowsResult.length === 0 && baselineWin) {
      const { data: countData } = await supabase.rpc('cost_dashboard_baseline_line_count', {
        p_company_id: companyId,
        p_start_date: baselineWin.start,
        p_end_date:   baselineWin.end,
      });
      setBaselineLineCount(typeof countData === 'number' ? countData : 0);
    }

    setRunning(false);
  }

  // ── Vendor picker ─────────────────────────────────────────────────────────
  const toggleVendor = (vendor) => {
    setVendorFilter(prev => {
      // Switching off "All" picks no vendors initially. The operator picks
      // explicitly. Toggling a vendor when "All" is on flips to picked-only.
      if (prev.all) {
        return { all: false, picked: new Set([vendor]) };
      }
      const next = new Set(prev.picked);
      if (next.has(vendor)) next.delete(vendor); else next.add(vendor);
      // If they deselect everything, treat as "All" so they don't see an
      // empty result by accident — per spec.
      if (next.size === 0) return { all: true, picked: new Set() };
      return { all: false, picked: next };
    });
  };
  const setAllVendors = () => setVendorFilter({ all: true, picked: new Set() });

  // ── Apply filters + sort ──────────────────────────────────────────────────
  const visibleRows = useMemo(() => {
    if (!rows) return null;

    let filtered = [...rows];

    // Vendor filter
    if (!vendorFilter.all) {
      filtered = filtered.filter(r => vendorFilter.picked.has(r.primary_vendor));
    }

    // Polarity filter (interpreted per report type)
    if (polarity !== 'all') {
      if (reportType === 'top-spend') {
        // For spend: Up = latest_unit_price > avg_unit_price; Down = lower.
        filtered = filtered.filter(r => {
          const latest = Number(r.latest_unit_price);
          const avg = Number(r.avg_unit_price);
          if (!isFinite(latest) || !isFinite(avg) || avg === 0) return false;
          return polarity === 'up' ? latest > avg : latest < avg;
        });
      } else {
        // For movers: trend_direction is the source of truth.
        filtered = filtered.filter(r => r.trend_direction === polarity);
      }
    }

    // Sort
    if (reportType === 'top-spend') {
      if (sort === 'unit')          filtered.sort((a, b) => Number(b.latest_unit_price || 0) - Number(a.latest_unit_price || 0));
      else if (sort === 'invoices') filtered.sort((a, b) => Number(b.invoice_count || 0) - Number(a.invoice_count || 0));
      else                          filtered.sort((a, b) => Number(b.total_spend || 0) - Number(a.total_spend || 0));
    } else {
      if (sort === 'pct')           filtered.sort((a, b) => Math.abs(Number(b.delta_pct || 0)) - Math.abs(Number(a.delta_pct || 0)));
      else if (sort === 'volume')   filtered.sort((a, b) => Number(b.total_volume_in_window || 0) - Number(a.total_volume_in_window || 0));
      else                          filtered.sort((a, b) => Math.abs(Number(b.dollar_impact || 0)) - Math.abs(Number(a.dollar_impact || 0)));
    }

    return filtered;
  }, [rows, reportType, vendorFilter, polarity, sort]);

  // Cap notice — only show when "All" was picked AND the cap was reached.
  const capNoticeText = (
    rowCount === 'all' && rows && rows.length >= ROW_COUNT_CAP
      ? `Showing ${ROW_COUNT_CAP} of ${rows.length}+ items. Narrow the time range or filter by vendor for finer detail.`
      : null
  );

  // ── Add-to-watchlist handler (Top spend rows have a "+ Watchlist" button) ──
  const handleAddToWatchlist = async (foodItemId) => {
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from('cost_watchlist_items').insert({
      company_id: companyId,
      food_item_id: foodItemId,
      added_by: user?.id || null,
    });
    // Optimistic local update so the row's "+ Watchlist" flips immediately.
    setRows(prev => prev?.map(r => r.food_item_id === foodItemId ? { ...r, on_watchlist: true } : r));
    onWatchlistChange?.();
  };

  // ── CSV export ────────────────────────────────────────────────────────────
  // Parent (Invoices.js) owns the actual download; we hand back the rows
  // and a suggested filename. Same csvCell helper at the call site.
  const exportPayload = useMemo(() => {
    if (!visibleRows || visibleRows.length === 0 || !lastRanRange) return null;
    return {
      reportType,
      rows: visibleRows,
      windowStart: lastRanRange.start,
      windowEnd: lastRanRange.end,
    };
  }, [visibleRows, reportType, lastRanRange]);

  return (
    <div className="space-y-4">
      {/* Controls panel */}
      <div
        className="rounded-lg p-4 no-print"
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          {/* Left: configuration */}
          <div className="space-y-3 flex-1 min-w-0">
            <ControlRow label="Time range">
              <SegmentedPills
                value={rangeKey}
                onChange={setRangeKey}
                options={TIME_RANGE_OPTIONS}
                size="sm"
              />
              {rangeKey === 'custom' && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                  <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} style={dateInputStyle} />
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>to</span>
                  <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} style={dateInputStyle} />
                  {customRangeInvalid && (
                    <span style={{ fontSize: 11, color: 'var(--text-critical)' }}>Start must be before end</span>
                  )}
                </div>
              )}
            </ControlRow>

            {/* Comparison window — Top Movers only. Top Spend doesn't need
                a comparison concept (rankings are absolute, not delta-based). */}
            {reportType === 'top-movers' && (
              <ComparisonRow
                comparison={comparison}
                onComparisonChange={setComparison}
                compStart={compStart}
                compEnd={compEnd}
                onCompStartChange={setCompStart}
                onCompEndChange={setCompEnd}
                reportStart={startDateForRange(rangeKey, customStart)}
                reportEnd={rangeKey === 'custom' && customEnd ? customEnd : todayIso()}
                invalid={compRangeInvalid}
                inFuture={compInFuture}
                needsDates={compNeedsDates}
              />
            )}

            <ControlRow label="Row count">
              <SegmentedPills value={rowCount} onChange={setRowCount} options={ROW_COUNT_OPTIONS} size="sm" />
            </ControlRow>

            <ControlRow label="Polarity">
              <SegmentedPills value={polarity} onChange={setPolarity} options={POLARITY_OPTIONS} size="sm" />
            </ControlRow>

            <ControlRow label="Sort">
              <SegmentedPills
                value={sort}
                onChange={setSort}
                options={reportType === 'top-spend' ? SORT_TOP_SPEND : SORT_TOP_MOVERS}
                size="sm"
              />
            </ControlRow>

            <ControlRow label="Vendors">
              <VendorPicker
                vendors={vendors}
                value={vendorFilter}
                onToggle={toggleVendor}
                onAll={setAllVendors}
              />
            </ControlRow>
          </div>

          {/* Right: actions */}
          <div className="flex flex-col items-end gap-2">
            <PrimaryBtn
              onClick={() => runReport()}
              disabled={running || customRangeInvalid || compRangeInvalid || compInFuture || compNeedsDates}
            >
              {running ? 'Running…' : 'Run report'}
            </PrimaryBtn>
            <div className="flex items-center gap-2">
              <SecondaryBtn
                onClick={() => window.print()}
                disabled={!visibleRows || visibleRows.length === 0}
                className="!text-xs !px-3 !py-1.5"
                title={!visibleRows ? 'Run a report first' : undefined}
              >
                Print
              </SecondaryBtn>
              <SecondaryBtn
                onClick={() => downloadCsv(exportPayload)}
                disabled={!exportPayload}
                className="!text-xs !px-3 !py-1.5"
                title={!exportPayload ? 'Run a report first' : undefined}
              >
                Export CSV
              </SecondaryBtn>
            </div>
            {staleConfig && (
              <div className="text-xs" style={{ color: 'var(--text-warning)' }}>
                Config changed — click Run to refresh
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Result panel */}
      <div
        data-invoice-section
        className="rounded-lg"
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
      >
        {rows == null && !running && (
          <div className="p-12 text-center">
            <div className="text-3xl mb-3" aria-hidden>📈</div>
            <div className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
              {reportType === 'top-spend' ? 'Top spend report' : 'Top movers report'}
            </div>
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
              {reportType === 'top-spend'
                ? 'Highest-cost items in the chosen window. Click Run report to populate.'
                : 'Items with the largest dollar impact (price change × volume). Click Run report to populate.'}
            </div>
          </div>
        )}

        {(running || rows != null) && (
          <ReportResultHeader
            reportType={reportType}
            range={lastRanRange}
            count={visibleRows?.length}
            running={running}
          />
        )}

        {reportType === 'top-spend' && (
          <TopSpend
            rows={visibleRows}
            loading={running}
            onOpenDrillIn={onOpenDrillIn}
            onAddToWatchlist={handleAddToWatchlist}
            capNoticeText={capNoticeText}
          />
        )}

        {reportType === 'top-movers' && (() => {
          // Empty-baseline state takes priority: when the movers RPC returned
          // 0 rows AND the baseline-window count helper confirmed 0 lines,
          // show the actionable empty state with comparison-flip CTAs
          // instead of the generic "no comparable price movement" message.
          const showEmptyBaseline =
            !running
            && rows != null
            && rows.length === 0
            && baselineLineCount === 0
            && lastBaselineWindow;
          if (showEmptyBaseline) {
            return (
              <EmptyBaselineState
                baselineWindow={lastBaselineWindow}
                onSwitchToSameLastYear={() => {
                  setComparison('sameLastYear');
                  runReport({ comparison: 'sameLastYear' });
                }}
                onSwitchToCustom={() => {
                  // Pre-fill custom dates with the baseline-of-prior-period
                  // dates so the picker isn't empty when the operator gets
                  // to it. They can tweak from there. Don't auto-run since
                  // a custom-range pick is inherently "operator decides".
                  if (lastBaselineWindow) {
                    setCompStart(lastBaselineWindow.start);
                    setCompEnd(lastBaselineWindow.end);
                  }
                  setComparison('custom');
                }}
              />
            );
          }
          return (
            <TopMovers
              rows={visibleRows}
              loading={running}
              onOpenDrillIn={onOpenDrillIn}
              capNoticeText={capNoticeText}
            />
          );
        })()}
      </div>
    </div>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────────────

const dateInputStyle = {
  background: 'var(--bg-subtle)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: '4px 8px',
  fontSize: 12,
  color: 'var(--text-primary)',
};

// ComparisonRow — Top Movers' "COMPARED TO" control block. Carries the
// segmented selector, the optional custom-range pickers, the InfoDot
// explainer, and the subtle warning text for overlap / single-day /
// future / inverted ranges.
function ComparisonRow({
  comparison, onComparisonChange,
  compStart, compEnd, onCompStartChange, onCompEndChange,
  reportStart, reportEnd,
  invalid, inFuture, needsDates,
}) {
  // Subtle warnings — fire only when a custom range is picked + valid, so
  // they don't shout while the operator is mid-input.
  const overlapsReport =
    comparison === 'custom' && compStart && compEnd && !invalid && !inFuture
    && compStart <= reportEnd && compEnd >= reportStart;
  const singleDay =
    comparison === 'custom' && compStart && compEnd && !invalid && !inFuture
    && compStart === compEnd;

  // Resolved label — what comparison window the RPC will actually use.
  // Helps the operator confirm "yes, that's what I meant" before clicking
  // Run. We mirror the server's auto-derivation for 'prior' so the
  // displayed window matches what the server will compute.
  const resolved = (() => {
    if (!reportStart || !reportEnd) return null;
    const w = resolveBaselineWindow(reportStart, reportEnd, comparison, compStart, compEnd);
    return w;
  })();

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 11, fontWeight: 500, textTransform: 'uppercase',
        letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 6,
      }}>
        <span>Compared to</span>
        <InfoDot>{COMPARISON_INFO}</InfoDot>
      </div>
      <SegmentedPills
        value={comparison}
        onChange={onComparisonChange}
        options={COMPARISON_OPTIONS}
        size="sm"
      />
      {comparison === 'custom' && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="date" value={compStart}
            onChange={(e) => onCompStartChange(e.target.value)}
            style={dateInputStyle}
          />
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>to</span>
          <input
            type="date" value={compEnd}
            onChange={(e) => onCompEndChange(e.target.value)}
            style={dateInputStyle}
          />
          {invalid && (
            <span style={{ fontSize: 11, color: 'var(--text-critical)' }}>
              Start must be before end
            </span>
          )}
          {!invalid && inFuture && (
            <span style={{ fontSize: 11, color: 'var(--text-critical)' }}>
              Comparison window can't be in the future
            </span>
          )}
          {!invalid && !inFuture && needsDates && (
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              Pick both dates to enable Run report
            </span>
          )}
        </div>
      )}
      {/* Resolved-window readout + subtle warnings */}
      {resolved && (
        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          {`Window: ${resolved.start} — ${resolved.end}`}
          {overlapsReport && (
            <span style={{ marginLeft: 8, color: 'var(--text-warning)' }}>
              · Overlaps your report window — results may be less meaningful
            </span>
          )}
          {singleDay && (
            <span style={{ marginLeft: 8, color: 'var(--text-warning)' }}>
              · Only one day of data — baseline may be noisy
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// EmptyBaselineState — fires when the movers RPC returned 0 rows AND the
// baseline-window count helper confirmed 0 lines in the comparison window.
// The two CTAs flip the comparison radio + auto-rerun (or open the picker
// for Custom). Distinct from TopMovers' own "no comparable price movement"
// empty state which fires when the comparison HAD data but nothing moved.
function EmptyBaselineState({ baselineWindow, onSwitchToSameLastYear, onSwitchToCustom }) {
  return (
    <div className="p-12 text-center" style={{ borderTop: '1px solid var(--border)' }}>
      <div className="text-3xl mb-3" aria-hidden>📭</div>
      <div className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
        Need invoice history to compute movers
      </div>
      <div className="text-xs mb-4" style={{ color: 'var(--text-muted)', lineHeight: 1.6, maxWidth: 380, margin: '0 auto 16px' }}>
        No invoices found in your comparison window:
        {' '}<span style={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
          {baselineWindow.start} – {baselineWindow.end}
        </span>.
        {' '}Try a different comparison, or come back after more invoices are uploaded.
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
        <button
          onClick={onSwitchToSameLastYear}
          style={ctaBtnStyle}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-card)'; }}
        >
          Same period last year
        </button>
        <button
          onClick={onSwitchToCustom}
          style={ctaBtnStyle}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-card)'; }}
        >
          Custom range
        </button>
      </div>
    </div>
  );
}

const ctaBtnStyle = {
  padding: '6px 14px', fontSize: 12, fontWeight: 600,
  background: 'var(--bg-card)', color: 'var(--text-primary)',
  border: '1px solid var(--border)', borderRadius: 8,
  cursor: 'pointer',
};

function ControlRow({ label, children }) {
  return (
    <div>
      <div style={{
        fontSize: 11, fontWeight: 500, textTransform: 'uppercase',
        letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 6,
      }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function VendorPicker({ vendors, value, onToggle, onAll }) {
  const [open, setOpen] = useState(false);
  const summary = value.all
    ? 'All vendors'
    : value.picked.size === 1
      ? Array.from(value.picked)[0].replace(/\n/g, ' · ')
      : `${value.picked.size} vendors`;

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          padding: '5px 10px', fontSize: 12, fontWeight: 500,
          background: 'var(--bg-subtle)', border: '1px solid var(--border)',
          borderRadius: 6, color: 'var(--text-primary)', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 6,
          maxWidth: 280,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</span>
        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>▾</span>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          <div style={{
            position: 'absolute', top: '100%', left: 0, marginTop: 4,
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            zIndex: 41, minWidth: 240, maxHeight: 280, overflowY: 'auto',
            padding: 4,
          }}>
            <button
              onClick={onAll}
              style={{
                width: '100%', textAlign: 'left',
                padding: '6px 10px', fontSize: 12,
                background: value.all ? 'var(--bg-accent-chip)' : 'transparent',
                color: value.all ? 'var(--accent-primary)' : 'var(--text-primary)',
                border: 'none', borderRadius: 6, cursor: 'pointer',
                display: 'block',
              }}
            >
              All vendors
            </button>
            <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
            {vendors.length === 0 && (
              <div style={{ padding: 8, fontSize: 12, color: 'var(--text-muted)' }}>No vendors found</div>
            )}
            {vendors.map(v => {
              const checked = !value.all && value.picked.has(v);
              return (
                <button
                  key={v}
                  onClick={() => onToggle(v)}
                  style={{
                    width: '100%', textAlign: 'left',
                    padding: '6px 10px', fontSize: 12,
                    background: checked ? 'var(--bg-accent-chip)' : 'transparent',
                    color: checked ? 'var(--accent-primary)' : 'var(--text-primary)',
                    border: 'none', borderRadius: 6, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}
                  onMouseEnter={(e) => { if (!checked) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                  onMouseLeave={(e) => { if (!checked) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{
                    width: 12, height: 12, borderRadius: 3,
                    border: '1px solid var(--border)',
                    background: checked ? 'var(--accent-primary)' : 'transparent',
                    flexShrink: 0,
                  }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {v.replace(/\n/g, ' · ')}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function ReportResultHeader({ reportType, range, count, running }) {
  return (
    <div
      data-invoice-section
      className="px-4 py-3"
      style={{ borderBottom: '1px solid var(--border)' }}
    >
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
        {reportType === 'top-spend' ? 'Top spend' : 'Top movers'}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
          {running ? 'Running…' : range ? `${range.start} → ${range.end}` : '—'}
        </div>
        {!running && count != null && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {count} item{count === 1 ? '' : 's'}
          </div>
        )}
      </div>
    </div>
  );
}

// Filename: top-spend-30d-2026-04-30.csv (per spec).
function suggestedFilename({ reportType, windowStart, windowEnd }) {
  const slug = reportType === 'top-spend' ? 'top-spend' : 'top-movers';
  return `${slug}-${windowStart}-to-${windowEnd}.csv`;
}

// CSV downloader. Uses the same csvCell helper exported from Invoices.js.
// Imported here lazily because Invoices.js is the parent surface that owns
// the export contract; we just take the rows and headers and stream them.
function downloadCsv(payload) {
  if (!payload) return;
  const { reportType, rows, windowStart, windowEnd } = payload;

  let header, lineFor;
  if (reportType === 'top-spend') {
    header = ['rank','item_name','primary_vendor','total_spend','invoice_count','avg_unit_price','latest_unit_price','on_watchlist'];
    lineFor = (r, idx) => [
      idx + 1,
      csvCell(r.item_name),
      csvCell(r.primary_vendor),
      Number(r.total_spend || 0).toFixed(2),
      r.invoice_count || 0,
      Number(r.avg_unit_price || 0).toFixed(4),
      Number(r.latest_unit_price || 0).toFixed(4),
      r.on_watchlist ? 'true' : 'false',
    ].join(',');
  } else {
    header = ['rank','item_name','primary_vendor','baseline_unit_price','current_unit_price','delta_pct','total_volume','dollar_impact','trend_direction','consecutive_increases'];
    lineFor = (r, idx) => [
      idx + 1,
      csvCell(r.item_name),
      csvCell(r.primary_vendor),
      Number(r.baseline_unit_price || 0).toFixed(4),
      Number(r.current_unit_price || 0).toFixed(4),
      Number(r.delta_pct || 0).toFixed(2),
      Number(r.total_volume_in_window || 0).toFixed(2),
      Number(r.dollar_impact || 0).toFixed(2),
      csvCell(r.trend_direction),
      r.consecutive_increases || 0,
    ].join(',');
  }

  const lines = [header.join(','), ...rows.map(lineFor)];
  const csv = lines.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = suggestedFilename({ reportType, windowStart, windowEnd });
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
